// @figma file=tbd node=tbd name="AuditLogTable" set="araf-analytics" verified=tbd
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import * as React from 'react';
import { Badge } from '../components/atoms/badge';
import { Button } from '../components/atoms/button';
import { EmptyState } from '../components/atoms/empty-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/atoms/table';
import { cn } from '../lib/utils';
import type { AuditLogRecordDto } from './types';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AuditLogTableProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Audit log rows */
  records: AuditLogRecordDto[];
  /** Total record count (for pagination display) */
  total: number;
  /** Current page (1-based) */
  page: number;
  /** Records per page */
  pageSize: number;
  /** Called when the user navigates to a new page */
  onPageChange: (page: number) => void;
  /**
   * Scope table to a single tenant.
   * Pass undefined to show tenantSlug column (cross-tenant admin view).
   */
  tenantSlug?: string;
  /** Render skeleton rows */
  loading?: boolean;
}

// ─── Diff viewer ─────────────────────────────────────────────────────────────

function DiffViewer({
  before,
  after,
}: {
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
}) {
  if (!before && !after) return <span className="text-xs text-muted-foreground">—</span>;

  return (
    <div className="grid grid-cols-2 gap-3 pt-2 text-xs font-mono">
      {before !== undefined && before !== null && (
        <div>
          <div className="mb-1 text-muted-foreground uppercase tracking-wide text-[10px]">
            Before
          </div>
          <pre className="rounded bg-destructive/10 text-destructive p-2 overflow-auto max-h-40 whitespace-pre-wrap">
            {JSON.stringify(before, null, 2)}
          </pre>
        </div>
      )}
      {after !== undefined && after !== null && (
        <div>
          <div className="mb-1 text-muted-foreground uppercase tracking-wide text-[10px]">
            After
          </div>
          <pre className="rounded bg-success/10 text-success p-2 overflow-auto max-h-40 whitespace-pre-wrap">
            {JSON.stringify(after, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const AuditLogTable = React.forwardRef<HTMLDivElement, AuditLogTableProps>(
  (
    {
      className,
      records,
      total,
      page,
      pageSize,
      onPageChange,
      tenantSlug,
      loading = false,
      ...props
    },
    ref,
  ) => {
    const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
    const showTenantColumn = tenantSlug === undefined;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const hasDiff = (r: AuditLogRecordDto) => r.before != null || r.after != null;

    function toggleExpand(id: string) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    }

    const colCount = showTenantColumn ? 6 : 5;

    return (
      <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
        <div className="overflow-auto rounded-[var(--radius)] border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <span className="sr-only">Expand</span>
                </TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>User</TableHead>
                {showTenantColumn && <TableHead>Tenant</TableHead>}
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: pageSize > 10 ? 10 : pageSize }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: colCount }).map((__, j) => (
                      <TableCell key={j}>
                        <div className="h-4 w-full rounded bg-muted animate-pulse" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="py-10">
                    <EmptyState
                      heading="No audit records"
                      description="No records match the current filters."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                records.map((r) => (
                  <React.Fragment key={r.id}>
                    <TableRow
                      className={cn(hasDiff(r) && 'cursor-pointer hover:bg-muted/40')}
                      onClick={() => hasDiff(r) && toggleExpand(r.id)}
                      role={hasDiff(r) ? 'button' : undefined}
                      tabIndex={hasDiff(r) ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (hasDiff(r) && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          toggleExpand(r.id);
                        }
                      }}
                    >
                      <TableCell className="w-8 pr-0">
                        {hasDiff(r) ? (
                          expandedIds.has(r.id) ? (
                            <ChevronDownIcon className="size-4 text-muted-foreground" />
                          ) : (
                            <ChevronRightIcon className="size-4 text-muted-foreground" />
                          )
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          appearance="outline"
                          className="font-mono text-xs"
                        >
                          {r.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-foreground">
                        <span>{r.resource}</span>
                        {r.resourceId && (
                          <span className="ml-1 text-xs text-muted-foreground font-mono">
                            #{r.resourceId}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm text-foreground">{r.userEmail ?? r.userId}</span>
                          {r.userEmail && (
                            <span className="text-xs text-muted-foreground font-mono">
                              {r.userId}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      {showTenantColumn && (
                        <TableCell>
                          <Badge variant="secondary" appearance="soft">
                            {r.tenantSlug}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(r.timestamp).toLocaleString()}
                      </TableCell>
                    </TableRow>
                    {expandedIds.has(r.id) && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={colCount} className="px-4 pb-4">
                          <DiffViewer before={r.before} after={r.after} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
          <span>
            {total === 0
              ? 'No records'
              : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-1">
            <Button
              appearance="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </Button>
            <span className="px-2 tabular-nums">
              {page} / {totalPages}
            </span>
            <Button
              appearance="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  },
);
AuditLogTable.displayName = 'AuditLogTable';

export { AuditLogTable };
